/**
 * Extracts a user-friendly error message from Django REST Framework (DRF) error responses.
 *
 * DRF returns errors in various formats. This function handles all common patterns:
 *
 * 1. Field-specific validation errors (from serializers):
 *    - { "password": ["This password is too short."] }
 *    - { "password": "This password is too short." }
 *    - { "confirm_password": ["Passwords do not match."] }
 *
 * 2. API-level errors (from views/services like FunnelPasswordService):
 *    - { "detail": "The email is associated with a member" }
 *    - { "detail": "The email is not associated with a lead" }
 *    - { "detail": "The workflow is not running" }
 *
 * 3. Non-field validation errors (from serializers):
 *    - { "non_field_errors": ["Unable to log in with provided credentials."] }
 *
 * 4. Simple string errors:
 *    - "Invalid request"
 *
 * Example error object structure (from axios):
 * {
 *   response: {
 *     data: { "detail": "The email is associated with a member" }
 *   }
 * }
 *
 * @param error - The error object from an API call (typically axios error)
 * @param priorityFields - Optional array of field names to check first (in order of priority)
 *                         Example: ['password', 'confirm_password'] will check password errors before confirm_password
 * @param defaultMessage - Default message to return if no error message can be extracted
 * @returns A user-friendly error message string
 *
 * @example
 * // Priority fields example
 * extractApiErrorMessage(
 *   error,
 *   ['password', 'confirm_password'],
 *   'There was an error setting your password. Please try again.'
 * );
 *
 * @example
 * // Service error (detail field)
 * const error = {
 *   response: {
 *     data: { detail: "The email is associated with a member" }
 *   }
 * };
 * extractApiErrorMessage(error); // Returns: "The email is associated with a member"
 *
 * @example
 * // Field validation error
 * const error = {
 *   response: {
 *     data: { password: ["This password is too short."] }
 *   }
 * };
 * extractApiErrorMessage(error, ['password']); // Returns: "This password is too short."
 */
export default function extractApiErrorMessage(
    error: unknown,
    priorityFields: string[] = [],
    defaultMessage = 'An error occurred. Please try again.'
): string {
    const errorData = (error as { response?: { data?: unknown } })?.response?.data;

    // No error data available
    if (!errorData) {
        return defaultMessage;
    }

    // Handle string error messages
    if (typeof errorData === 'string') {
        return errorData;
    }

    // Check priority fields first (e.g., 'password', 'confirm_password')
    // These are field-specific validation errors from serializers
    const priorityFieldError = priorityFields
        .map((field) => {
            const fieldError = (errorData as Record<string, unknown>)?.[field];
            if (fieldError) {
                const errors = Array.isArray(fieldError) ? fieldError : [fieldError];
                if (errors.length > 0 && errors[0]) {
                    return errors[0] as string;
                }
            }
            return null;
        })
        .find((errorMsg) => errorMsg !== null);

    if (priorityFieldError) {
        return priorityFieldError;
    }

    // Check 'detail' field (API-level errors from views/services, e.g., FunnelPasswordService)
    const detailError = (errorData as { detail?: unknown })?.detail;
    if (detailError) {
        const errors = Array.isArray(detailError) ? detailError : [detailError];
        if (errors.length > 0 && errors[0]) {
            return errors[0] as string;
        }
    }

    // Check non_field_errors (serializer-level non-field validation errors)
    const nonFieldErrors = (errorData as { non_field_errors?: unknown })?.non_field_errors;
    if (nonFieldErrors) {
        const errors = Array.isArray(nonFieldErrors) ? nonFieldErrors : [nonFieldErrors];
        if (errors.length > 0 && errors[0]) {
            return errors[0] as string;
        }
    }

    // Fallback: use first available error message from any field
    const firstError = Object.values(errorData as Record<string, unknown>)[0];
    if (firstError) {
        const errors = Array.isArray(firstError) ? firstError : [firstError];
        if (errors.length > 0 && errors[0]) {
            return errors[0] as string;
        }
    }

    return defaultMessage;
}
